import {
  storeOverviewReportResponseSchema,
  storeOverviewResponseSchema,
  temporaryStoreOverviewConversationResponseSchema,
} from "@/features/store-overview/storeOverviewSchemas";
import { baseApi } from "@/shared/api/baseApi";

export interface StoreOverviewOwner {
  organizationId: string;
  userId: string;
}

export interface StoreOverviewQuery extends StoreOverviewOwner {
  displayCurrency?: string;
  end?: string;
  range: "today" | "7d" | "30d" | "custom";
  start?: string;
}

interface SnapshotMutation extends StoreOverviewOwner {
  snapshotId: string;
}

interface CreateReportInput extends SnapshotMutation {
  idempotencyKey: string;
}

interface StoreOverviewReportQuery extends StoreOverviewOwner {
  jobId: string;
}

export interface TemporaryConversationScope extends StoreOverviewOwner {
  conversationId: string;
}

function path(organizationId: string) {
  return `/v1/organizations/${encodeURIComponent(organizationId)}/merchant-home/store-overview`;
}

export function storeOverviewRequest(input: StoreOverviewQuery) {
  return {
    params: {
      display_currency: input.displayCurrency,
      end_date: input.range === "custom" ? input.end : undefined,
      range: input.range,
      start_date: input.range === "custom" ? input.start : undefined,
    },
    url: path(input.organizationId),
  };
}

export function parseStoreOverviewQueryData(
  response: unknown,
  input: StoreOverviewQuery,
) {
  let parsed: ReturnType<typeof storeOverviewResponseSchema.parse>;
  try {
    parsed = storeOverviewResponseSchema.parse(response);
  } catch {
    throw new Error("store_overview_schema_invalid");
  }
  if (parsed.queryScope.rangeKind !== input.range) {
    throw new Error("store_overview_range_identity_mismatch");
  }
  if (
    input.displayCurrency &&
    parsed.queryScope.displayCurrency !== input.displayCurrency
  ) {
    throw new Error("store_overview_currency_identity_mismatch");
  }
  if (
    input.range === "custom" &&
    parsed.queryScope.anchorDate !== `${input.start}/${input.end}`
  ) {
    throw new Error("store_overview_custom_range_identity_mismatch");
  }
  return parsed;
}

export const storeOverviewApi = baseApi
  .enhanceEndpoints({ addTagTypes: ["StoreOverview", "StoreOverviewReport"] })
  .injectEndpoints({
    endpoints: (builder) => ({
      closeTemporaryStoreOverviewConversation: builder.mutation<
        void,
        TemporaryConversationScope
      >({
        invalidatesTags: (_result, _error, input) => [
          {
            id: `${input.userId}:${input.organizationId}`,
            type: "Conversations",
          },
        ],
        query: ({ conversationId, organizationId }) => ({
          method: "DELETE",
          responseHandler: (response) =>
            response.status === 204 ? Promise.resolve(undefined) : response.json(),
          url: `/v1/organizations/${encodeURIComponent(organizationId)}/temporary-conversations/${encodeURIComponent(conversationId)}`,
          validateStatus: (response) => response.status === 204,
        }),
      }),
      createStoreOverviewReport: builder.mutation<
        ReturnType<typeof storeOverviewReportResponseSchema.parse>,
        CreateReportInput
      >({
        invalidatesTags: (_result, _error, input) => [
          { id: input.snapshotId, type: "StoreOverviewReport" },
        ],
        query: ({ idempotencyKey, organizationId, snapshotId }) => ({
          body: {
            idempotency_key: idempotencyKey,
            snapshot_id: snapshotId,
          },
          method: "POST",
          url: `${path(organizationId)}/reports`,
        }),
        transformResponse: (response) =>
          storeOverviewReportResponseSchema.parse(response),
      }),
      createTemporaryStoreOverviewConversation: builder.mutation<
        ReturnType<
          typeof temporaryStoreOverviewConversationResponseSchema.parse
        >,
        SnapshotMutation
      >({
        invalidatesTags: (_result, _error, input) => [
          {
            id: `${input.userId}:${input.organizationId}`,
            type: "Conversations",
          },
        ],
        query: ({ organizationId, snapshotId }) => ({
          body: { snapshot_id: snapshotId },
          method: "POST",
          url: `${path(organizationId)}/temporary-conversations`,
        }),
        transformResponse: (response) =>
          temporaryStoreOverviewConversationResponseSchema.parse(response),
      }),
      getStoreOverview: builder.query<
        ReturnType<typeof storeOverviewResponseSchema.parse>,
        StoreOverviewQuery
      >({
        keepUnusedDataFor: 0,
        providesTags: (result, _error, input) => [
          {
            id: result?.snapshotId ?? `${input.userId}:${input.organizationId}`,
            type: "StoreOverview",
          },
          "MerchantStoreBindings",
        ],
        query: storeOverviewRequest,
        // RTK 调用签名是 (response, meta, arg)，不能把 parse 函数直接当 transform 传入。
        transformResponse: (response, _meta, input) =>
          parseStoreOverviewQueryData(response, input),
      }),
      getStoreOverviewReport: builder.query<
        ReturnType<typeof storeOverviewReportResponseSchema.parse>,
        StoreOverviewReportQuery
      >({
        providesTags: (result, _error, input) => [
          {
            id: result?.jobId ?? input.jobId,
            type: "StoreOverviewReport",
          },
        ],
        query: ({ jobId, organizationId }) => ({
          url: `${path(organizationId)}/reports/${encodeURIComponent(jobId)}`,
        }),
        transformResponse: (response) =>
          storeOverviewReportResponseSchema.parse(response),
      }),
    }),
  });

export const {
  useCloseTemporaryStoreOverviewConversationMutation,
  useCreateStoreOverviewReportMutation,
  useCreateTemporaryStoreOverviewConversationMutation,
  useGetStoreOverviewQuery,
  useLazyGetStoreOverviewReportQuery,
} = storeOverviewApi;
